import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, fmt, money } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Plus, Users, Eye, Heart, Loader2, Pencil, Trash2, History, AlertTriangle, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const PLATFORMS = [
  { key: "youtube", label: "YouTube", color: "#FF0000" },
  { key: "tiktok", label: "TikTok", color: "#FF0050" },
  { key: "instagram", label: "Instagram", color: "#E1306C" },
];
const TIMEFRAMES = [{ v: "1d", l: "1 Day" }, { v: "1w", l: "1 Week" }, { v: "1m", l: "1 Month" }, { v: "ytd", l: "YTD" }];
const pubRatio = (v) => (v?.views ? Math.round((v.likes / v.views) * 10000) / 100 : 0);

export default function Marketing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [pubVideos, setPubVideos] = useState([]);
  const [platform, setPlatform] = useState("all");
  const [timeframe, setTimeframe] = useState("1m");
  const [metric, setMetric] = useState("followers");
  const [series, setSeries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ platform: "youtube", date: new Date().toISOString().slice(0, 10), followers: "", views: "", likes: "" });
  const [snapshots, setSnapshots] = useState([]);
  const [histPlatform, setHistPlatform] = useState("all");
  const [editSnap, setEditSnap] = useState(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadSummary = useCallback(() => {
    api.get(`/marketing/summary?platform=${platform}`).then((r) => setSummary(r.data));
  }, [platform]);

  const loadSeries = useCallback(() => {
    api.get(`/marketing/timeseries?platform=${platform}&metric=${metric}&timeframe=${timeframe}`).then((r) => setSeries(r.data));
  }, [platform, metric, timeframe]);

  const loadSnapshots = useCallback(() => {
    api.get(`/marketing/snapshots`).then((r) => setSnapshots(r.data));
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadSeries(); }, [loadSeries]);
  useEffect(() => { loadSnapshots(); }, [loadSnapshots]);
  useEffect(() => { api.get("/videos").then((r) => setPubVideos(r.data.filter((v) => v.status === "published"))); }, []);

  const refreshAll = () => { loadSummary(); loadSeries(); loadSnapshots(); };

  const submit = async () => {
    await api.post("/marketing/snapshots", {
      platform: form.platform, date: form.date,
      followers: Number(form.followers) || 0, views: Number(form.views) || 0, likes: Number(form.likes) || 0,
    });
    toast.success("Metrics recorded");
    setOpen(false);
    refreshAll();
  };

  const saveEdit = async () => {
    await api.patch(`/marketing/snapshots/${editSnap.id}`, {
      platform: editSnap.platform, date: editSnap.date,
      followers: Number(editSnap.followers) || 0, views: Number(editSnap.views) || 0, likes: Number(editSnap.likes) || 0,
    });
    toast.success("Entry updated");
    setEditSnap(null);
    refreshAll();
  };

  const deleteSnap = async (id) => {
    if (!window.confirm("Delete this metrics entry? This cannot be undone.")) return;
    await api.delete(`/marketing/snapshots/${id}`);
    toast.success("Entry deleted");
    refreshAll();
  };

  const clearSample = async () => {
    setClearing(true);
    try {
      const { data } = await api.post(`/marketing/clear-sample-data`);
      toast.success(`Sample data cleared — ${data.snapshots} metrics, ${data.tasks} tasks, ${data.milestones} milestones removed`);
      setClearOpen(false);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clear sample data");
    } finally {
      setClearing(false);
    }
  };

  const totals = summary?.totals;
  const g = totals?.growth?.[timeframe];

  const statCards = totals ? [
    { label: "Followers", value: fmt(totals.followers), icon: Users, delta: g?.followers, color: "#F59E0B" },
    { label: "Total Views", value: fmt(totals.views), icon: Eye, delta: g?.views, color: "#06B6D4" },
    { label: "Likes / View Ratio", value: `${totals.likes_view_ratio}%`, icon: Heart, delta: null, color: "#FF0050", testid: "likes-view-ratio-card" },
    { label: "Promotion Spend", value: money(summary?.promotion_spend?.total || 0), icon: DollarSign, delta: null, color: "#10B981", testid: "promotion-spend-card" },
  ] : [];

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="marketing-dashboard">
      <PageHeader title="Marketing" subtitle="Live growth across YouTube, TikTok and Instagram.">
        <Button onClick={() => setOpen(true)} data-testid="add-metrics-button" className="font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> Add Metrics
        </Button>
      </PageHeader>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-6" data-testid="marketing-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History className="w-3.5 h-3.5 mr-1.5" /> History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger className="w-44" data-testid="platform-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Platforms</SelectItem>
            {PLATFORMS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-1 bg-secondary/50 p-1 rounded-lg">
          {TIMEFRAMES.map((t) => (
            <button key={t.v} onClick={() => setTimeframe(t.v)} data-testid={`timeframe-filter-${t.v}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${timeframe === t.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {!summary ? (
        <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {statCards.map((c) => (
              <div key={c.label} className="glass border border-border rounded-2xl p-5" data-testid={c.testid}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{c.label}</span>
                  <c.icon className="w-4 h-4" style={{ color: c.color }} />
                </div>
                <p className="font-display text-3xl font-bold">{c.value}</p>
                {c.delta !== null && c.delta !== undefined && (
                  <p className={`text-xs mt-2 flex items-center gap-1 ${c.delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {c.delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {c.delta >= 0 ? "+" : ""}{fmt(c.delta)} in {TIMEFRAMES.find((t) => t.v === timeframe).l}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="glass border border-border rounded-2xl p-6 mb-6" data-testid="overall-growth-chart">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-lg">Growth Over Time</h3>
              <Select value={metric} onValueChange={setMetric}>
                <SelectTrigger className="w-36" data-testid="metric-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="followers">Followers</SelectItem>
                  <SelectItem value="views">Views</SelectItem>
                  <SelectItem value="likes">Likes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 11 }} tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} tickFormatter={fmt} width={45} />
                <Tooltip contentStyle={{ background: "#12161F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#F8FAFC" }} formatter={(v) => fmt(v)} />
                <Area type="monotone" dataKey="value" stroke="#F59E0B" strokeWidth={2.5} fill="url(#grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(summary.platforms).map(([p, d]) => {
              const meta = PLATFORMS.find((x) => x.key === p);
              const pg = d.growth?.[timeframe];
              return (
                <div key={p} className="glass border border-border rounded-2xl p-5" data-testid={`platform-card-${p}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-3 h-3 rounded-full" style={{ background: meta.color }} />
                    <h4 className="font-display font-semibold">{meta.label}</h4>
                  </div>
                  <div className="space-y-2.5 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Followers</span><span className="font-mono">{fmt(d.followers)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Views</span><span className="font-mono">{fmt(d.views)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Likes/View</span><span className="font-mono text-primary">{d.likes_view_ratio}%</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Promo Spend</span><span className="font-mono text-emerald-400">{money(summary.promotion_spend?.per_platform?.[p] || 0)}</span></div>
                    {pg && <div className="flex justify-between pt-2 border-t border-border"><span className="text-muted-foreground">Follower Growth</span>
                      <span className={`font-mono ${pg.followers >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pg.followers >= 0 ? "+" : ""}{fmt(pg.followers)} ({pg.followers_pct}%)</span></div>}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="glass border border-border rounded-2xl p-6 mt-6" data-testid="published-content-panel">
            <div className="flex items-center gap-2 mb-4">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <h3 className="font-display font-semibold text-lg">Live Published Content</h3>
              <span className="text-xs text-muted-foreground">{pubVideos.length} live</span>
            </div>
            {pubVideos.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No published videos yet. Mark a video as <span className="text-emerald-400 font-medium">Published</span> in the Video Library and its live stats appear here.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...pubVideos].sort((a, b) => pubRatio(b) - pubRatio(a)).map((v) => (
                  <button key={v.id} onClick={() => navigate(`/videos?open=${v.id}`)} data-testid={`published-video-${v.id}`}
                    className="text-left bg-secondary/40 border border-border rounded-xl p-4 hover:border-primary/40 hover:bg-secondary/60 transition-all">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]">● Live</Badge>
                      <Badge variant="secondary" className="capitalize text-[10px]">{v.video_type}</Badge>
                    </div>
                    <p className="font-medium text-sm truncate mb-3">{v.title}</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><span className="text-muted-foreground block">Views</span><span className="font-mono">{fmt(v.views || 0)}</span></div>
                      <div><span className="text-muted-foreground block">Likes</span><span className="font-mono">{fmt(v.likes || 0)}</span></div>
                      <div><span className="text-muted-foreground block">L/V</span><span className="font-mono text-primary">{pubRatio(v)}%</span></div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
        </TabsContent>

        <TabsContent value="history">
          <div className="glass border border-border rounded-2xl p-6" data-testid="history-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div>
                <h3 className="font-display font-semibold text-lg">Metrics History</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Every recorded snapshot. Edit a mistake or delete an entry. Seeded sample rows are tagged.</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={histPlatform} onValueChange={setHistPlatform}>
                  <SelectTrigger className="w-40" data-testid="history-platform-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Platforms</SelectItem>
                    {PLATFORMS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {user?.role === "admin" && (
                  <Button variant="outline" onClick={() => setClearOpen(true)} data-testid="clear-sample-data-button"
                    className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 font-semibold">
                    <AlertTriangle className="w-4 h-4 mr-1.5" /> Clear sample data
                  </Button>
                )}
              </div>
            </div>

            {(() => {
              const rows = [...snapshots]
                .filter((s) => histPlatform === "all" || s.platform === histPlatform)
                .sort((a, b) => (a.date < b.date ? 1 : -1));
              if (rows.length === 0) {
                return <p className="text-sm text-muted-foreground py-8 text-center">No metrics recorded yet. Use "Add Metrics" to log your first snapshot.</p>;
              }
              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="history-table">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                        <th className="py-2.5 pr-3 font-semibold">Date</th>
                        <th className="py-2.5 pr-3 font-semibold">Platform</th>
                        <th className="py-2.5 pr-3 font-semibold text-right">Followers</th>
                        <th className="py-2.5 pr-3 font-semibold text-right">Views</th>
                        <th className="py-2.5 pr-3 font-semibold text-right">Likes</th>
                        <th className="py-2.5 pr-3 font-semibold"></th>
                        <th className="py-2.5 font-semibold text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((s) => {
                        const meta = PLATFORMS.find((x) => x.key === s.platform);
                        return (
                          <tr key={s.id} className="border-b border-border/50 hover:bg-secondary/30 transition-colors" data-testid={`history-row-${s.id}`}>
                            <td className="py-2.5 pr-3 font-mono text-xs">{new Date(s.date).toLocaleDateString()}</td>
                            <td className="py-2.5 pr-3">
                              <span className="inline-flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta?.color || "#888" }} />
                                {meta?.label || s.platform}
                              </span>
                            </td>
                            <td className="py-2.5 pr-3 text-right font-mono">{fmt(s.followers)}</td>
                            <td className="py-2.5 pr-3 text-right font-mono">{fmt(s.views)}</td>
                            <td className="py-2.5 pr-3 text-right font-mono">{fmt(s.likes)}</td>
                            <td className="py-2.5 pr-3">{s.seed && <Badge variant="secondary" className="text-[10px]" data-testid="history-sample-badge">Sample</Badge>}</td>
                            <td className="py-2.5 text-right whitespace-nowrap">
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditSnap({ ...s })} data-testid={`history-edit-${s.id}`}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => deleteSnap(s.id)} data-testid={`history-delete-${s.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </TabsContent>
      </Tabs>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-border">
          <DialogHeader><DialogTitle className="font-display">Record Metrics Snapshot</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Enter today's numbers from each platform. Growth is calculated automatically over time.</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Platform</Label>
                <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                  <SelectTrigger data-testid="snapshot-platform-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Followers</Label><Input type="number" value={form.followers} onChange={(e) => setForm({ ...form, followers: e.target.value })} data-testid="snapshot-followers-input" /></div>
            <div className="space-y-2"><Label>Total Views</Label><Input type="number" value={form.views} onChange={(e) => setForm({ ...form, views: e.target.value })} data-testid="snapshot-views-input" /></div>
            <div className="space-y-2"><Label>Total Likes</Label><Input type="number" value={form.likes} onChange={(e) => setForm({ ...form, likes: e.target.value })} data-testid="snapshot-likes-input" /></div>
          </div>
          <DialogFooter><Button onClick={submit} data-testid="snapshot-submit" className="font-semibold">Save Snapshot</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit snapshot dialog */}
      <Dialog open={!!editSnap} onOpenChange={(o) => !o && setEditSnap(null)}>
        <DialogContent className="glass border-border" data-testid="edit-snapshot-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit Metrics Entry</DialogTitle></DialogHeader>
          {editSnap && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Platform</Label>
                  <Select value={editSnap.platform} onValueChange={(v) => setEditSnap({ ...editSnap, platform: v })}>
                    <SelectTrigger data-testid="edit-platform-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Date</Label><Input type="date" value={editSnap.date} onChange={(e) => setEditSnap({ ...editSnap, date: e.target.value })} data-testid="edit-date-input" /></div>
              </div>
              <div className="space-y-2"><Label>Followers</Label><Input type="number" value={editSnap.followers} onChange={(e) => setEditSnap({ ...editSnap, followers: e.target.value })} data-testid="edit-followers-input" /></div>
              <div className="space-y-2"><Label>Total Views</Label><Input type="number" value={editSnap.views} onChange={(e) => setEditSnap({ ...editSnap, views: e.target.value })} data-testid="edit-views-input" /></div>
              <div className="space-y-2"><Label>Total Likes</Label><Input type="number" value={editSnap.likes} onChange={(e) => setEditSnap({ ...editSnap, likes: e.target.value })} data-testid="edit-likes-input" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} data-testid="edit-snapshot-save" className="font-semibold">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear sample data confirmation */}
      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="glass border-border" data-testid="clear-sample-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-400" /> Clear sample data?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This permanently removes all the demo data seeded into your account — sample marketing metrics, demo tasks and demo milestones. Anything you created yourself is kept. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setClearOpen(false)} data-testid="clear-cancel">Cancel</Button>
            <Button onClick={clearSample} disabled={clearing} data-testid="clear-confirm" className="bg-red-500 hover:bg-red-600 text-white font-semibold">
              {clearing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Clearing...</> : "Clear sample data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
